import { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '../../components/owner/PageHeader';
import GymIdMemberSearch from '../../components/receptionist/GymIdMemberSearch';
import { formatCurrency } from './receptionistData';
import { getGymMemberId, isPendingGymMemberId } from '../admin/members/memberData';
import { MEMBER_SEARCH_LABEL, MEMBER_SEARCH_PLACEHOLDER } from './sales/salePageConfig';
import { fetchMembersPage, fetchRemainingPayments } from '../../services/receptionist/receptionistApi';
import { PAYMENTS_UPDATE_EVENT } from './paymentStore';
import { dedupeMembers } from '../../utils/dedupeList';
import { usePanelToast } from '../../context/PanelToastContext';
import PaySelectedBalancesModal from './sales/PaySelectedBalancesModal';
import SaleReceiptModal from './sales/SaleReceiptModal';
import { remainingPaymentCategory } from './sales/remainingPaymentCategories';
import {
  saleRowLeft,
  saleRowPaid,
  saleRowTotal,
  saleRowHasRemainingBalance,
  isPaymentWalkIn,
} from './sales/salePaymentUtils';
import { saleRowDuration, saleRowPackageName } from './sales/salePeriodUtils';
import '../owner/ownerPages.css';
import '../owner/subscriptions/subscriptions.css';
import '../owner/finance/finance.css';
import './members/members.css';
import './sales.css';
import './sales/ancillarySales.css';
import './remainingPayments.css';

function saleDate(row) {
  return String(row.date || '').slice(0, 10) || '—';
}

function rowKey(row) {
  return String(row.rawId || row.id || '');
}

function rowSerial(row) {
  if (row.isWalkIn || isPaymentWalkIn(row) || isPendingGymMemberId(row.gymId)) return '—';
  return getGymMemberId(row) || row.gymId || '—';
}

export default function RemainingPayments() {
  const toast = usePanelToast();
  const [memberHits, setMemberHits] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState({ name: '', gymId: '', member: null });
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [payRows, setPayRows] = useState(null);
  const [receiptQueue, setReceiptQueue] = useState([]);

  const loadAllPayments = useCallback(async () => {
    try {
      setError('');
      setLoading(true);
      const rows = await fetchRemainingPayments();
      setPayments(rows || []);
    } catch (err) {
      setError(err.message || 'Failed to load remaining payments');
      setPayments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAllPayments();
  }, [loadAllPayments]);

  useEffect(() => {
    const refresh = () => loadAllPayments();
    window.addEventListener(PAYMENTS_UPDATE_EVENT, refresh);
    return () => window.removeEventListener(PAYMENTS_UPDATE_EVENT, refresh);
  }, [loadAllPayments]);

  // Search members as user types (for picking a customer to focus)
  useEffect(() => {
    let cancelled = false;
    const q = query.trim();
    if (!q || selected.member) {
      setMemberHits(selected.member ? [selected.member] : []);
      return undefined;
    }
    const timer = setTimeout(async () => {
      try {
        const result = await fetchMembersPage({ search: q, pageSize: 25 });
        if (!cancelled) setMemberHits(dedupeMembers(result.rows || []));
      } catch {
        if (!cancelled) setMemberHits([]);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, selected.member]);

  const allPending = useMemo(
    () => (payments || [])
      .filter((row) => saleRowHasRemainingBalance(row))
      .sort((a, b) => String(b.date || '').localeCompare(String(a.date || ''))),
    [payments],
  );

  const memberSelected = Boolean(selected.member?.id);

  // Member select filters; otherwise free-text search filters the full list
  const visiblePending = useMemo(() => {
    if (memberSelected) {
      const mid = String(selected.member.id);
      return allPending.filter((row) => String(row.memberId || row.member_id || '') === mid);
    }
    const q = query.trim().toLowerCase();
    if (!q) return allPending;
    return allPending.filter((row) => {
      const type = remainingPaymentCategory(row).toLowerCase();
      return (
        String(row.member || '').toLowerCase().includes(q)
        || String(row.gymId || '').toLowerCase().includes(q)
        || String(getGymMemberId(row) || '').toLowerCase().includes(q)
        || String(row.package || '').toLowerCase().includes(q)
        || String(row.paymentNote || '').toLowerCase().includes(q)
        || String(row.invoiceId || '').toLowerCase().includes(q)
        || type.includes(q)
        || saleDate(row).includes(q)
      );
    });
  }, [allPending, memberSelected, selected.member, query]);

  const selectedSerial = getGymMemberId(selected.member)
    || (selected.gymId && !isPendingGymMemberId(selected.gymId) ? selected.gymId : '');

  const visibleTotalLeft = useMemo(
    () => visiblePending.reduce((sum, row) => sum + saleRowLeft(row), 0),
    [visiblePending],
  );

  const selectedPendingRows = useMemo(
    () => visiblePending.filter((row) => selectedKeys.has(rowKey(row))),
    [visiblePending, selectedKeys],
  );

  const selectedTotalLeft = useMemo(
    () => selectedPendingRows.reduce((sum, row) => sum + saleRowLeft(row), 0),
    [selectedPendingRows],
  );

  const allVisibleSelected = visiblePending.length > 0
    && visiblePending.every((row) => selectedKeys.has(rowKey(row)));

  useEffect(() => {
    const valid = new Set(visiblePending.map(rowKey));
    setSelectedKeys((prev) => {
      const next = new Set([...prev].filter((key) => valid.has(key)));
      if (next.size === prev.size) return prev;
      return next;
    });
  }, [visiblePending]);

  const handleMemberSelect = (next) => {
    const gymId = isPendingGymMemberId(next?.gymId) ? '' : (next?.gymId || '');
    setSelected({ ...next, gymId });
    setSelectedKeys(new Set());
  };

  const clearMemberFilter = () => {
    setSelected({ name: '', gymId: '', member: null });
    setQuery('');
    setMemberHits([]);
    setSelectedKeys(new Set());
  };

  const toggleRow = (row) => {
    const key = rowKey(row);
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      setSelectedKeys(new Set());
      return;
    }
    setSelectedKeys(new Set(visiblePending.map(rowKey)));
  };

  const handlePaySelected = () => {
    if (!selectedPendingRows.length) {
      toast.showError('Select at least one pending sale to pay.');
      return;
    }
    setPayRows(selectedPendingRows);
  };

  const handlePaid = async (_updatedRows, receipts = []) => {
    setSelectedKeys(new Set());
    await loadAllPayments();
    const list = Array.isArray(receipts) ? receipts.filter(Boolean) : [];
    if (list.length) setReceiptQueue(list);
  };

  const activeReceipt = receiptQueue[0] || null;
  const closeReceipt = () => {
    setReceiptQueue((prev) => prev.slice(1));
  };

  const showMemberCols = !memberSelected;

  return (
    <div className="rec-ancillary-sale rec-remaining-payments">
      <PageHeader
        title="Remaining Payments"
        subtitle="All pending balances — search or pick a member to filter"
      >
        <span className="owner-badge owner-badge-warning">
          {visiblePending.length} pending · {formatCurrency(visibleTotalLeft)} left
        </span>
      </PageHeader>

      {loading && <p className="owner-placeholder-text owner-mb-16">Loading…</p>}
      {error && (
        <p className="owner-placeholder-text owner-mb-16" style={{ color: 'var(--owner-danger, #dc2626)' }}>
          {error}
        </p>
      )}

      <div className="owner-card rec-panel-card rec-ancillary-sale-form-card">
        <h2 className="owner-card-title">Search / filter</h2>
        <div className="subscription-form rec-ancillary-sale-form">
          <div className="rec-ancillary-sale-grid">
            <div className="owner-form-group rec-ancillary-sale-full">
              <GymIdMemberSearch
                id="remaining-payments-member"
                label={MEMBER_SEARCH_LABEL}
                query={query}
                onQueryChange={setQuery}
                selectedName={selected.name}
                selectedGymId={selected.gymId}
                selectedMember={selected.member}
                onSelect={handleMemberSelect}
                members={memberHits}
                serverFiltered
                placeholder={MEMBER_SEARCH_PLACEHOLDER}
              />
              {query.trim() && !selected.member && !memberHits.length && (
                <p className="owner-placeholder-text" style={{ marginTop: 8, fontSize: 12 }}>
                  No members match this search — list below still filters by what you typed.
                </p>
              )}
              {memberSelected && (
                <p className="owner-placeholder-text" style={{ marginTop: 8, fontSize: 12 }}>
                  Showing pending balances for{' '}
                  <strong>{selected.name || selected.member?.name || '—'}</strong>
                  {selectedSerial ? <> · Serial No <strong>{selectedSerial}</strong></> : null}
                  {' · '}
                  <button
                    type="button"
                    className="owner-btn owner-btn-secondary owner-btn-sm"
                    onClick={clearMemberFilter}
                    style={{ marginLeft: 4 }}
                  >
                    Show all
                  </button>
                </p>
              )}
            </div>
          </div>

          <div className="rec-remaining-pay-bar">
            <label className="rec-remaining-select-all">
              <input
                type="checkbox"
                checked={allVisibleSelected}
                onChange={toggleSelectAll}
                disabled={visiblePending.length === 0}
              />
              Select all ({visiblePending.length})
            </label>
            <p className="owner-placeholder-text" style={{ margin: 0 }}>
              Selected: <strong>{selectedPendingRows.length}</strong>
              {' · '}
              To pay: <strong className="finance-amount-negative">{formatCurrency(selectedTotalLeft)}</strong>
            </p>
            <button
              type="button"
              className="owner-btn owner-btn-primary"
              onClick={handlePaySelected}
              disabled={selectedPendingRows.length === 0}
            >
              Pay selected
            </button>
          </div>
        </div>
      </div>

      <div className="owner-card rec-panel-card rec-member-list-card">
        <div className="rec-ancillary-sale-history-header">
          <h2 className="owner-card-title">
            {memberSelected ? 'Pending balances' : 'All pending balances'}
          </h2>
          <p className="owner-placeholder-text" style={{ margin: 0 }}>
            {visiblePending.length} sale{visiblePending.length === 1 ? '' : 's'} · {formatCurrency(visibleTotalLeft)} pending
            {!memberSelected && allPending.length !== visiblePending.length ? (
              <> · filtered from {allPending.length}</>
            ) : null}
          </p>
        </div>
        <div className="owner-table-wrap">
          <table className="owner-table rec-member-table">
            <thead>
              <tr>
                <th style={{ width: 48 }}>Pay</th>
                <th>Date</th>
                {showMemberCols && <th>Serial No</th>}
                {showMemberCols && <th>Member</th>}
                <th>Type</th>
                <th>Sale / Package</th>
                <th>Duration</th>
                <th>Total</th>
                <th>Paid</th>
                <th>Left</th>
                <th>Invoice</th>
                <th>Note</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visiblePending.length > 0 ? visiblePending.map((row) => {
                const key = rowKey(row);
                const checked = selectedKeys.has(key);
                const type = remainingPaymentCategory(row);
                return (
                  <tr key={key} className={checked ? 'rec-remaining-row-selected' : ''}>
                    <td>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleRow(row)}
                        aria-label={`Select ${type} dated ${saleDate(row)}`}
                      />
                    </td>
                    <td>{saleDate(row)}</td>
                    {showMemberCols && (
                      <td className="rec-member-gym-id">{rowSerial(row)}</td>
                    )}
                    {showMemberCols && <td>{row.member || '—'}</td>}
                    <td>{type}</td>
                    <td>{saleRowPackageName(row)}</td>
                    <td>{saleRowDuration(row)}</td>
                    <td>{formatCurrency(saleRowTotal(row))}</td>
                    <td className="finance-amount-positive">{formatCurrency(saleRowPaid(row))}</td>
                    <td className="finance-amount-negative">{formatCurrency(saleRowLeft(row))}</td>
                    <td>{row.invoiceId || '—'}</td>
                    <td>{row.paymentNote || '—'}</td>
                    <td>
                      <button
                        type="button"
                        className="owner-btn owner-btn-primary owner-btn-sm"
                        onClick={() => setPayRows([row])}
                      >
                        Pay this
                      </button>
                    </td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan={showMemberCols ? 13 : 11} className="owner-placeholder-text">
                    {loading
                      ? 'Loading…'
                      : allPending.length === 0
                        ? 'No remaining payments. All customers are fully paid.'
                        : 'No pending payments match this search.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {payRows?.length > 0 && (
        <PaySelectedBalancesModal
          key={payRows.map(rowKey).join('|')}
          paymentRows={payRows}
          categoryLabel={
            payRows.length === 1
              ? remainingPaymentCategory(payRows[0])
              : `${payRows.length} selected`
          }
          onClose={() => setPayRows(null)}
          onPaid={handlePaid}
        />
      )}

      {activeReceipt && (
        <SaleReceiptModal receipt={activeReceipt} onClose={closeReceipt} />
      )}
    </div>
  );
}
